import axios from 'axios';

class UsageTracker {
  constructor() {
    this.storageKey = 'meal_calculator_usage';
    this.maxFreeCalculations = 3; // Changed from 5 to 3
    this.serverApiEndpoint = '/.netlify/functions/track-usage';
  }

  // Get user fingerprint (combination of IP-like data and browser info)
  getUserFingerprint() {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Browser fingerprint', 2, 2);

    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset(),
      canvas.toDataURL(),
      navigator.hardwareConcurrency || 'unknown',
      navigator.deviceMemory || 'unknown'
    ].join('|');

    // Create a simple hash
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    return Math.abs(hash).toString(36);
  }

  // Get usage data from localStorage with server validation
  async getUsageData() {
    try {
      // First check local storage
      const localData = localStorage.getItem(this.storageKey);
      const localUsage = localData ? JSON.parse(localData) : {};

      // Try to validate with server (if available)
      try {
        const fingerprint = this.getUserFingerprint();
        const response = await axios.get(`${this.serverApiEndpoint}?fingerprint=${fingerprint}`);
        if (response.data && response.data.usage) {
          // Update local storage with server data
          const serverUsage = response.data.usage;
          localUsage[fingerprint] = serverUsage;
          this.saveUsageData(localUsage);
          return localUsage;
        }
      } catch (serverError) {
        // If server validation fails, fall back to local data
        console.log('Server validation unavailable, using local data');
      }

      return localUsage;
    } catch (error) {
      console.error('Error reading usage data:', error);
      return {};
    }
  }

  // Save usage data to localStorage and attempt to sync with server
  async saveUsageData(data) {
    try {
      // Save to localStorage
      localStorage.setItem(this.storageKey, JSON.stringify(data));

      // Try to sync with server
      const fingerprint = this.getUserFingerprint();
      const userData = data[fingerprint];

      try {
        await axios.post(this.serverApiEndpoint, {
          fingerprint,
          usage: userData
        });
      } catch (serverError) {
        // If server sync fails, that's ok - we have local data
        console.log('Server sync unavailable');
      }
    } catch (error) {
      console.error('Error saving usage data:', error);
    }
  }

  // Get current user's usage stats
  async getCurrentUserUsage() {
    const fingerprint = this.getUserFingerprint();
    const allUsage = await this.getUsageData();
    
    // Check if user is logged in and has premium status
    const userData = localStorage.getItem('meal_calculator_user');
    let isPremium = false;
    
    if (userData) {
      const user = JSON.parse(userData);
      // Check if user has premium status (this would be set after payment)
      isPremium = user.isPremium || false;
    }
    
    const usage = allUsage[fingerprint] || {
      successfulCalculations: 0,
      totalCalculations: 0,
      firstUse: Date.now(),
      lastUse: Date.now(),
      hasPaid: isPremium,
      paymentDate: isPremium ? Date.now() : null
    };
    
    // If user is premium, update the usage record
    if (isPremium && !usage.hasPaid) {
      usage.hasPaid = true;
      usage.paymentDate = Date.now();
      allUsage[fingerprint] = usage;
      await this.saveUsageData(allUsage);
    }
    
    return usage;
  }

  // Record a successful calculation (within threshold)
  async recordSuccessfulCalculation() {
    const fingerprint = this.getUserFingerprint();
    const allUsage = await this.getUsageData();
    const userUsage = allUsage[fingerprint] || this.getEmptyUserRecord();

    userUsage.successfulCalculations += 1;
    userUsage.totalCalculations += 1;
    userUsage.lastUse = Date.now();

    allUsage[fingerprint] = userUsage;
    await this.saveUsageData(allUsage);

    return userUsage;
  }

  // Record any calculation attempt
  async recordCalculationAttempt() {
    const fingerprint = this.getUserFingerprint();
    const allUsage = await this.getUsageData();
    const userUsage = allUsage[fingerprint] || this.getEmptyUserRecord();

    userUsage.totalCalculations += 1;
    userUsage.lastUse = Date.now();

    allUsage[fingerprint] = userUsage;
    await this.saveUsageData(allUsage);

    return userUsage;
  }

  // Get empty user record
  getEmptyUserRecord() {
    return {
      successfulCalculations: 0,
      totalCalculations: 0,
      firstUse: Date.now(),
      lastUse: Date.now(),
      hasPaid: false,
      paymentDate: null
    };
  }

  // Check if user needs to pay
  async needsPayment() {
    const usage = await this.getCurrentUserUsage();
    return usage.successfulCalculations >= this.maxFreeCalculations && !usage.hasPaid;
  }

  // Record payment (only after verification)
  async recordPayment(paymentId = null) {
    const fingerprint = this.getUserFingerprint();
    const allUsage = await this.getUsageData();
    const userUsage = allUsage[fingerprint] || this.getEmptyUserRecord();

    userUsage.hasPaid = true;
    userUsage.paymentDate = Date.now();
    userUsage.paymentId = paymentId;

    allUsage[fingerprint] = userUsage;
    await this.saveUsageData(allUsage);

    // Also update user record if logged in
    const userData = localStorage.getItem('meal_calculator_user');
    if (userData) {
      const user = JSON.parse(userData);
      user.isPremium = true;
      user.premiumDate = new Date().toISOString();
      localStorage.setItem('meal_calculator_user', JSON.stringify(user));
    }

    return userUsage;
  }

  // Get remaining free calculations
  async getRemainingFreeCalculations() {
    const usage = await this.getCurrentUserUsage();
    if (usage.hasPaid) return Infinity;
    return Math.max(0, this.maxFreeCalculations - usage.successfulCalculations);
  }

  // Check if calculation was successful (within thresholds)
  isCalculationSuccessful(mealPlan, targets, thresholds) {
    if (!mealPlan || !targets) return false;

    const { totals } = mealPlan;
    const targetCals = parseFloat(targets.calories);

    // Check calorie threshold
    const calorieError = Math.abs(totals.calories - targetCals) / targetCals;
    const withinCalorieThreshold = calorieError <= (thresholds.calories / 100);

    if (!thresholds.advancedMode) {
      return withinCalorieThreshold;
    }

    // Check advanced mode thresholds
    const targetProtein = parseFloat(targets.protein) || 0;
    const targetCarbs = parseFloat(targets.carbs) || 0;
    const targetFat = parseFloat(targets.fat) || 0;

    const proteinOk = targetProtein === 0 || Math.abs(totals.protein - targetProtein) / targetProtein <= (thresholds.protein / 100);
    const carbsOk = targetCarbs === 0 || Math.abs(totals.carbs - targetCarbs) / targetCarbs <= (thresholds.carbs / 100);
    const fatOk = targetFat === 0 || Math.abs(totals.fat - targetFat) / targetFat <= (thresholds.fat / 100);

    return withinCalorieThreshold && proteinOk && carbsOk && fatOk;
  }

  // Clear all usage data (for testing)
  async clearUsageData() {
    localStorage.removeItem(this.storageKey);

    // Try to clear server data
    try {
      const fingerprint = this.getUserFingerprint();
      await axios.delete(`${this.serverApiEndpoint}?fingerprint=${fingerprint}`);
    } catch (error) {
      console.log('Server data clearing unavailable');
    }
  }

  // Verify payment with server (NEW METHOD)
  async verifyPaymentWithServer(sessionId) {
    try {
      // First verify with Stripe
      const response = await axios.get(`/.netlify/functions/verify-payment?session_id=${sessionId}`);
      
      if (response.data && response.data.verified) {
        // Payment is verified with Stripe, now record it locally
        await this.recordPayment(sessionId);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Payment verification failed:', error);
      return false;
    }
  }
}

export default new UsageTracker();